import React from 'react';
import { IconBrandGoogle } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';

import { auth, googleProvider } from '../Recipe/Firebase/InitialiseFirebase';
import { Button, Field, useToast } from '../../design/components';
import {
  AuthMode,
  friendlyAuthError,
  signInWithEmail,
  signInWithGoogle,
  signUpWithEmail,
} from './authHelpers';

/**
 * Full-page sign-in route. Reuses the same flows as <AuthDialog />
 * for environments / browsers where the modal is undesirable.
 */
const SignInScreen: React.FC = () => {
  const { show } = useToast();
  const navigate = useNavigate();
  const [mode, setMode] = React.useState<AuthMode>('signin');
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [inlineError, setInlineError] = React.useState<string | null>(null);

  const handleGoogle = async () => {
    setInlineError(null);
    setLoading(true);
    try {
      await signInWithGoogle(auth, googleProvider);
      navigate('/', { replace: true });
    } catch (err) {
      const msg = friendlyAuthError(err);
      show(msg, 'error');
      setInlineError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setInlineError(null);
    if (!email.trim() || !password) {
      setInlineError('Please enter an email and password.');
      return;
    }
    setLoading(true);
    try {
      if (mode === 'signin') {
        await signInWithEmail(auth, email.trim(), password);
      } else {
        await signUpWithEmail(auth, email.trim(), password);
      }
      navigate('/', { replace: true });
    } catch (err) {
      const msg = friendlyAuthError(err);
      show(msg, 'error');
      setInlineError(msg);
    } finally {
      setLoading(false);
    }
  };

  const submitLabel = mode === 'signin' ? 'Sign in' : 'Create account';

  return (
    <main className="rr-signin-screen">
      <div className="rr-signin-card rr-auth">
        <h1 className="rr-display rr-signin-title">Sign in</h1>
        <Button
          variant="primary"
          onClick={handleGoogle}
          disabled={loading}
          leftIcon={<IconBrandGoogle size={18} />}
          className="rr-auth-block"
          type="button"
        >
          Continue with Google
        </Button>

        <div className="rr-auth-divider" aria-hidden="true">
          <span>or</span>
        </div>

        <div className="rr-auth-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'signin'}
            className={[
              'rr-auth-tab',
              mode === 'signin' ? 'rr-auth-tab-active' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => setMode('signin')}
          >
            Sign in
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'signup'}
            className={[
              'rr-auth-tab',
              mode === 'signup' ? 'rr-auth-tab-active' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => setMode('signup')}
          >
            Create account
          </button>
        </div>

        <form onSubmit={handleEmailSubmit} className="rr-auth-form">
          <Field
            label="Email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            required
          />
          <Field
            label="Password"
            type="password"
            autoComplete={
              mode === 'signin' ? 'current-password' : 'new-password'
            }
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            required
          />
          {inlineError && (
            <div className="rr-auth-error" role="alert">
              {inlineError}
            </div>
          )}
          <Button
            variant="tomato"
            type="submit"
            loading={loading}
            disabled={loading}
            className="rr-auth-block"
          >
            {submitLabel}
          </Button>
        </form>
      </div>
    </main>
  );
};

export default SignInScreen;
